// Angular
import { ErrorHandler, Injectable, Injector, PLATFORM_ID, inject, DOCUMENT } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
// RxJs
import { Observable, of } from 'rxjs';
// Lodash
import { includes, noop, toString } from 'lodash';
// Sentry
import * as Sentry from '@sentry/angular';
// Environment
import { environment } from '@environment';
// Store
import { Store } from '@ngrx/store';
import { Logout } from '@app/core/auth';

@Injectable({
    providedIn: 'root'
})
export class GlobalErrorHandler implements ErrorHandler {
    private readonly document = inject<Document>(DOCUMENT);
    private readonly injector = inject(Injector);
    private readonly platformId = inject<Object>(PLATFORM_ID);

    /**
     * Handle failed operation and keep app alive
     */
    public handleError<T>(operation?: any, error?: any, isApiError: boolean = false): Observable<any> {
        if (!error && !operation) {
            return of([]);
        }

        // JWT expired / unauthorized
        if (error instanceof HttpErrorResponse && error.status === 401) {
            const store = this.injector.get(Store);
            alert('Your session has expired. Please relogin');
            store.dispatch(new Logout());
            return of(false);
        }

        // Skip HTML-as-JSON parsing errors
        if (isApiError && this.isHtmlApiResponse(error)) {
            return of(error);
        }

        // Skip cancelled/networkless browser errors
        if (error instanceof HttpErrorResponse && error.status === 0) {
            return of(error);
        }

        // Skip stale deployment chunk errors / import failures
        if (!isApiError && this.isChunkLoadError(error)) {
            return of(error);
        }

        // Skip aborted requests
        if (this.isAbortError(error)) {
            return of(error);
        }

        const errorMsg: string = this.constructErrorMsg(operation, error, isApiError);

        if (environment.isSentryLoggingEnable) {
            const skipLoggingStatusCodes: number[] = [400, 403, 404, 410, 422, 429, 451];

            if (isApiError && includes(skipLoggingStatusCodes, error?.status)) {
                noop();
            } else if (error instanceof Error) {
                // Prefer exception when raw Error object exists
                Sentry.captureException(error);
            } else {
                // Fallback for string/custom payload
                Sentry.captureMessage(errorMsg);
            }
        } else {
            console.error(errorMsg);
        }

        return of(error);
    }

    /**
     * Angular global error hook
     */
    public handleErrorSync(error: any): void {
        this.handleError('Unhandled application error', error, false);
    }

    /**
     * Aborted requests
     */
    private isAbortError(error: any): boolean {
        const message = error?.message || error?.toString?.() || '';

        return (
            error?.name === 'AbortError' ||
            message.includes('AbortError') ||
            message.includes('aborted a request')
        );
    }

    /**
     * Dynamic chunk / import load errors
     */
    private isChunkLoadError(error: any): boolean {
        const message = error?.message || error?.toString?.() || '';

        return (
            message.includes('error loading dynamically imported module') ||
            message.includes('Failed to fetch dynamically imported module') ||
            message.includes('Loading chunk') ||
            message.includes('ChunkLoadError') ||
            message.includes('text/html') ||
            message.includes('valid JavaScript MIME type')
        );
    }

    /**
     * Detect HTML instead of JSON
     */
    private isHtmlApiResponse(error: any): boolean {
        return (
            error instanceof HttpErrorResponse &&
            typeof error.message === 'string' &&
            error.message.includes('Http failure during parsing')
        );
    }

    /**
     * Create custom error message
     */
    private constructErrorMsg(operation: any, error: any, isApiError: boolean): string {
        const startMsg = isApiError ? 'API ERROR' : 'JS ERROR';
        const source = isPlatformBrowser(this.platformId) ? 'BROWSER' : 'SSR';
        let errorMsg = `${startMsg} | ${source} | ${operation}`;

        if (typeof error === 'string') {
            errorMsg += ` | ${error}`;
        }

        if (error?.message) {
            errorMsg += ` | ${error.message}`;
        }

        if (error?.stack) {
            errorMsg += ` | ${toString(error.stack)}`;
        }

        if (error instanceof HttpErrorResponse) {
            errorMsg += ` | ${error.url} | ${error.message} | ${error.error?.message} | ${error.error?.details}`;
        }

        if (error?.error instanceof Error) {
            errorMsg += ` | ${error.error.message}`;
        }

        if (isApiError) {
            errorMsg += ` | ${this.document.location.href}`;
        }

        if (operation && operation.type === 'error') {
            errorMsg = `JS | APPLICATION ERROR | ${operation?.message ?? operation?.type ?? 'Unknown application error'}`;
            console.log(errorMsg, operation, operation?.target, operation?.currentTarget);
        }

        return errorMsg;
    }
}